/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import { AnalyticsConfig, UserIdentity } from '../types';
import { sanitizeUserProperties, shouldInitializeAnalytics } from '../utils';

let sentryInitialized = false;

export const initSentry = (
  config: AnalyticsConfig,
  user?: UserIdentity
): void => {
  if (sentryInitialized) {
    return;
  }

  const shouldInit =
    shouldInitializeAnalytics(user) && config.sentry?.enabled !== false;

  if (shouldInit && config.sentry?.dsn) {
    try {
      Sentry.init({
        dsn: config.sentry.dsn,
        integrations: [
          Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes
          }),
          Sentry.browserTracingIntegration(),
          Sentry.browserProfilingIntegration(),
          Sentry.replayIntegration(),
          Sentry.captureConsoleIntegration({
            levels: ['error']
          }),
          Sentry.httpClientIntegration({
            failedRequestTargets: config.sentry?.failedRequestTargets || [
              /^https:\/\/.*\.mygrowdash\.com$/
            ]
          })
        ],
        tracesSampleRate: config.sentry?.tracesSampleRate || 1.0,
        tracePropagationTargets: config.sentry?.tracePropagationTargets || [
          /^https:\/\/.*\.mygrowdash\.com$/,
          'localhost'
        ],
        profilesSampleRate: config.sentry?.profilesSampleRate || 1.0,
        replaysSessionSampleRate:
          config.sentry?.replaysSessionSampleRate || 0.1,
        replaysOnErrorSampleRate: config.sentry?.replaysOnErrorSampleRate || 1.0
      });
      sentryInitialized = true;
    } catch (error) {
      console.warn('Failed to initialize Sentry:', error);
    }
  }
};

export const identifyUser = (user: UserIdentity): void => {
  if (!sentryInitialized) return;

  try {
    const properties = sanitizeUserProperties(user);
    Sentry.setUser({
      id: user.id,
      username: properties.username,
      email: properties.email
    });
  } catch (error) {
    console.warn('Sentry identify error:', error);
  }
};

export const captureException = (
  error: Error,
  context?: Record<string, any>
): void => {
  if (!sentryInitialized) return;

  try {
    if (context) {
      Sentry.withScope(scope => {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value);
        });
        Sentry.captureException(error);
      });
    } else {
      Sentry.captureException(error);
    }
  } catch (err) {
    console.warn('Sentry capture exception error:', err);
  }
};

export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info'
): void => {
  if (!sentryInitialized) return;

  try {
    Sentry.captureMessage(message, level);
  } catch (error) {
    console.warn('Sentry capture message error:', error);
  }
};

export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb): void => {
  if (!sentryInitialized) return;

  try {
    Sentry.addBreadcrumb(breadcrumb);
  } catch (error) {
    console.warn('Sentry breadcrumb error:', error);
  }
};

export const setContext = (key: string, context: Record<string, any>): void => {
  if (!sentryInitialized) return;

  try {
    Sentry.setContext(key, context);
  } catch (error) {
    console.warn('Sentry set context error:', error);
  }
};

export const clearContext = (): void => {
  if (!sentryInitialized) return;

  try {
    Sentry.setUser(null);
    Sentry.withScope(scope => {
      scope.clear();
    });
  } catch (error) {
    console.warn('Sentry clear context error:', error);
  }
};

export { Sentry };
export const getSentryInitialized = (): boolean => sentryInitialized;
